import { useCallback, useEffect, useRef, useState } from 'react'
import { autoUpdate, flip, offset, Placement, shift, useFloating, useTransitionStyles } from '@floating-ui/react'

import { PopupFloatingPortal } from '@/components/new/ui/popup-layer'

export const AppTooltip = () => {
    const [open, setOpen] = useState(false)
    const [content, setContent] = useState<string>('')
    const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null)
    const [placement, setPlacement] = useState<Placement>('top')

    const { x, y, strategy, refs, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    })

    const prefersReduced =
        typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: prefersReduced ? 0 : { open: 220, close: 120 },
        initial: { opacity: 0, transform: 'translateY(4px) scale(0.96)' },
        open: { opacity: 1, transform: 'translateY(0) scale(1)' },
        close: { opacity: 0, transform: 'translateY(0) scale(0.98)' },
    })

    const SHOW_DELAY = 500
    const HIDE_DELAY = 100

    const currentRef = useRef<HTMLElement | null>(null)
    const showTimer = useRef<number | null>(null)
    const hideTimer = useRef<number | null>(null)
    const perRefCleanup = useRef<(() => void) | null>(null)
    const sessionIdRef = useRef(0)
    const suppressFocusRef = useRef<HTMLElement | null>(null)

    const isTouchLike =
        typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window)

    useEffect(() => {
        if (referenceEl) refs.setReference(referenceEl)
    }, [referenceEl, refs])

    const clearShow = useCallback(() => {
        if (showTimer.current != null) {
            window.clearTimeout(showTimer.current)
            showTimer.current = null
        }
    }, [])

    const clearHide = useCallback(() => {
        if (hideTimer.current != null) {
            window.clearTimeout(hideTimer.current)
            hideTimer.current = null
        }
    }, [])

    const restoreTitle = useCallback((el: HTMLElement | null) => {
        if (!el) return

        const saved = el.getAttribute('data-original-title')

        if (saved != null) {
            el.setAttribute('title', saved)
            el.removeAttribute('data-original-title')
        }
    }, [])

    const detachPerRef = useCallback(() => {
        perRefCleanup.current?.()
        perRefCleanup.current = null
    }, [])

    const doHideNow = useCallback(() => {
        clearShow()
        clearHide()
        restoreTitle(currentRef.current)
        detachPerRef()
        currentRef.current = null
        setOpen(false)
        setReferenceEl(null)
        setContent('')
    }, [clearHide, clearShow, detachPerRef, restoreTitle])

    const scheduleHide = useCallback(() => {
        clearShow()
        clearHide()

        const scheduledForSession = sessionIdRef.current

        hideTimer.current = window.setTimeout(() => {
            if (sessionIdRef.current !== scheduledForSession) return
            doHideNow()
        }, HIDE_DELAY)
    }, [clearHide, clearShow, doHideNow])

    const attachPerRef = useCallback(
        (el: HTMLElement) => {
            const onLeave = () => scheduleHide()
            const onBlur = onLeave

            el.addEventListener('pointerleave', onLeave)
            el.addEventListener('blur', onBlur)

            const onKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') doHideNow()
            }
            const onWindowBlur = doHideNow
            const onVisibility = () => {
                if (document.visibilityState !== 'visible') doHideNow()
            }

            document.addEventListener('keydown', onKeyDown, true)
            window.addEventListener('blur', onWindowBlur)
            document.addEventListener('visibilitychange', onVisibility)

            perRefCleanup.current = () => {
                el.removeEventListener('pointerleave', onLeave)
                el.removeEventListener('blur', onBlur)
                document.removeEventListener('keydown', onKeyDown, true)
                window.removeEventListener('blur', onWindowBlur)
                document.removeEventListener('visibilitychange', onVisibility)
            }
        },
        [doHideNow, scheduleHide]
    )

    const getShowDelay = useCallback((el: HTMLElement): number => {
        const raw = el.getAttribute('data-title-delay')

        if (raw == null || raw === '') return SHOW_DELAY

        const n = Number(raw)

        if (!Number.isFinite(n)) return SHOW_DELAY

        return Math.max(0, n)
    }, [])

    const getDirection = useCallback((el: HTMLElement): Placement => {
        const dir = (el.getAttribute('data-title-direction') || 'bottom').toLowerCase()

        if (['top', 'bottom', 'left', 'right'].includes(dir)) {
            return dir as Placement
        }

        return 'top'
    }, [])

    const showNow = useCallback(
        (el: HTMLElement) => {
            if (currentRef.current && currentRef.current !== el) {
                restoreTitle(currentRef.current)
                detachPerRef()
            }

            const title = el.getAttribute('data-original-title') ?? el.getAttribute('title')

            if (!title) return

            if (el.hasAttribute('title')) {
                el.setAttribute('data-original-title', el.getAttribute('title') || '')
                el.removeAttribute('title')
            }

            currentRef.current = el
            attachPerRef(el)
            sessionIdRef.current += 1

            setReferenceEl(el)
            setPlacement(getDirection(el))
            setContent(title)
            setOpen(true)
        },
        [attachPerRef, detachPerRef, getDirection, restoreTitle]
    )

    const scheduleShow = useCallback(
        (el: HTMLElement) => {
            const hasActive = currentRef.current !== null

            clearHide()
            clearShow()

            if (hasActive && currentRef.current !== el) {
                showNow(el)
                return
            }

            if (hasActive && currentRef.current === el) return

            const delay = getShowDelay(el)

            if (delay === 0) {
                showNow(el)
                return
            }

            showTimer.current = window.setTimeout(() => showNow(el), delay)
        },
        [clearHide, clearShow, getShowDelay, showNow]
    )

    useEffect(() => {
        if (isTouchLike) return

        const onPointerOver = (e: Event) => {
            const pe = e as PointerEvent

            if ('pointerType' in pe && pe.pointerType === 'touch') return

            const target = (e.target as Element | null)?.closest<HTMLElement>('[title], [data-original-title]')

            if (!target) return

            scheduleShow(target)
        }

        const onPointerOut = (e: Event) => {
            const pe = e as PointerEvent

            if ('pointerType' in pe && pe.pointerType === 'touch') return

            const cur = currentRef.current

            if (!cur) {
                clearShow()
                return
            }

            const related = (e as MouseEvent).relatedTarget as Node | null

            if (related && cur.contains(related)) return

            scheduleHide()
        }

        const onFocusIn = (e: FocusEvent) => {
            const target = (e.target as Element | null)?.closest<HTMLElement>('[title], [data-original-title]')

            if (!target) return
            if (suppressFocusRef.current === target) return

            scheduleShow(target)
        }

        const onFocusOut = (e: FocusEvent) => {
            const cur = currentRef.current

            if (!cur) return

            const related = (e.relatedTarget as Node | null) ?? null

            if (related && cur.contains(related)) return

            scheduleHide()
        }

        const onPointerDown = (e: Event) => {
            const target = (e.target as Element | null)?.closest<HTMLElement>('[title], [data-original-title]')

            suppressFocusRef.current = target ?? null
            doHideNow()

            window.setTimeout(() => {
                if (suppressFocusRef.current === target) {
                    suppressFocusRef.current = null
                }
            }, 0)
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                doHideNow()
            }
        }

        document.addEventListener('pointerover', onPointerOver, true)
        document.addEventListener('pointerout', onPointerOut, true)
        document.addEventListener('pointerdown', onPointerDown, true)
        document.addEventListener('focusin', onFocusIn, true)
        document.addEventListener('focusout', onFocusOut, true)
        document.addEventListener('keydown', onKeyDown, true)

        const onDocMouseLeave = () => doHideNow()
        document.addEventListener('mouseleave', onDocMouseLeave)

        return () => {
            clearShow()
            clearHide()
            restoreTitle(currentRef.current)
            detachPerRef()
            currentRef.current = null

            document.removeEventListener('pointerover', onPointerOver, true)
            document.removeEventListener('pointerout', onPointerOut, true)
            document.removeEventListener('pointerdown', onPointerDown, true)
            document.removeEventListener('focusin', onFocusIn, true)
            document.removeEventListener('focusout', onFocusOut, true)
            document.removeEventListener('keydown', onKeyDown, true)
            document.removeEventListener('mouseleave', onDocMouseLeave)
        }
    }, [clearHide, clearShow, detachPerRef, doHideNow, isTouchLike, restoreTitle, scheduleHide, scheduleShow])

    return (
        <PopupFloatingPortal>
            {isMounted && content && (
                <div
                    ref={refs.setFloating}
                    role="tooltip"
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                        ...transitionStyles,
                    }}
                    className="pointer-events-none z-[60] whitespace-pre-wrap rounded-sm bg-neutral-900 px-2 py-1 text-xs font-medium leading-normal text-white dark:bg-neutral-100 dark:text-black"
                >
                    {content}
                </div>
            )}
        </PopupFloatingPortal>
    )
}
